import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Progress,
  Heading,
  UnorderedList,
  ListItem,
  Icon,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import { CheckCircle, AlertTriangle, Lightbulb, Sparkles, ArrowLeft, MessageCircle } from 'lucide-react';

interface RockEvaluationResult {
  overall_score: number;
  smart_score: number;
  clarity_score: number;
  scope_score: number;
  impact_score: number;
  action_score: number;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
  ai_rewrite_rock?: string;
  ai_rewrite_measurable?: string;
  ai_explanation?: string;
}

interface RockEvaluationResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: RockEvaluationResult | null;
  onShowRewrite: () => void;
  onBackToEditing: () => void;
  onOpenChat?: () => void;
  hasAcceptedTerms?: boolean;
}

/**
 * Rock Evaluation Results Modal Component
 * Displays AI evaluation results for a rock
 * Translated from Tailwind to Chakra UI
 */
export function RockEvaluationResultsModal({
  isOpen,
  onClose,
  result,
  onShowRewrite,
  onBackToEditing,
  onOpenChat,
  hasAcceptedTerms = true,
}: RockEvaluationResultsModalProps) {
  if (!result) return null;

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'green.600';
    if (score >= 75) return 'yellow.600';
    if (score >= 60) return 'orange.600';
    return 'red.600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return { bg: 'green.50', border: 'green.200' };
    if (score >= 75) return { bg: 'yellow.50', border: 'yellow.200' };
    if (score >= 60) return { bg: 'orange.50', border: 'orange.200' };
    return { bg: 'red.50', border: 'red.200' };
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent Rock!';
    if (score >= 75) return 'Strong Rock';
    if (score >= 60) return 'Needs Improvement';
    return 'Needs Significant Work';
  };

  const getBarColor = (score: number) => {
    if (score >= 90) return 'green';
    if (score >= 75) return 'yellow';
    if (score >= 60) return 'orange';
    return 'red';
  };

  const categories = [
    { name: 'SMART Criteria', score: result.smart_score },
    { name: 'Clarity & Focus', score: result.clarity_score },
    { name: 'Proper Scope', score: result.scope_score },
    { name: 'Meaningful Impact', score: result.impact_score },
    { name: 'Action-Oriented', score: result.action_score },
  ];

  const scoreBg = getScoreBgColor(result.overall_score);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2} justify="center">
            <Icon as={Sparkles} w={6} h={6} color="purple.600" />
            <Heading size="lg">Rock Quality Report</Heading>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Box
                display="inline-flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={8}
                border="2px"
                borderRadius="2xl"
                bg={scoreBg.bg}
                borderColor={scoreBg.border}
              >
                <Text fontSize="6xl" fontWeight="bold" color={getScoreColor(result.overall_score)}>
                  {result.overall_score}
                </Text>
                <Text fontSize="xl" fontWeight="semibold" color={getScoreColor(result.overall_score)}>
                  / 100
                </Text>
                <Text mt={2} fontSize="lg" fontWeight="medium" color="gray.700">
                  {getScoreLabel(result.overall_score)}
                </Text>
              </Box>
            </Box>

            <Box bg="gray.50" borderRadius="lg" p={6}>
              <Heading size="md" mb={4}>Category Scores</Heading>
              <VStack spacing={3} align="stretch">
                {categories.map((category) => (
                  <Box key={category.name}>
                    <HStack justify="space-between" fontSize="sm" mb={1}>
                      <Text fontWeight="medium" color="gray.700">
                        {category.name}
                      </Text>
                      <Text fontWeight="semibold" color={getScoreColor(category.score)}>
                        {category.score}%
                      </Text>
                    </HStack>
                    <Progress
                      value={category.score}
                      colorScheme={getBarColor(category.score)}
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>
                ))}
              </VStack>
            </Box>

            {result.strengths.length > 0 && (
              <Box bg="green.50" border="1px" borderColor="green.200" borderRadius="lg" p={6}>
                <HStack spacing={2} mb={3}>
                  <Icon as={CheckCircle} w={5} h={5} color="green.600" />
                  <Heading size="md" color="green.900">Strengths</Heading>
                </HStack>
                <UnorderedList spacing={2}>
                  {result.strengths.map((strength, index) => (
                    <ListItem key={index} color="green.800">
                      <HStack spacing={2} align="start">
                        <Text color="green.600" mt={0.5}>•</Text>
                        <Text>{strength}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            )}

            {result.weaknesses.length > 0 && (
              <Box bg="yellow.50" border="1px" borderColor="yellow.200" borderRadius="lg" p={6}>
                <HStack spacing={2} mb={3}>
                  <Icon as={AlertTriangle} w={5} h={5} color="yellow.600" />
                  <Heading size="md" color="yellow.900">Areas to Improve</Heading>
                </HStack>
                <UnorderedList spacing={2}>
                  {result.weaknesses.map((weakness, index) => (
                    <ListItem key={index} color="yellow.800">
                      <HStack spacing={2} align="start">
                        <Text color="yellow.600" mt={0.5}>•</Text>
                        <Text>{weakness}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            )}

            {result.recommendations.length > 0 && (
              <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="lg" p={6}>
                <HStack spacing={2} mb={3}>
                  <Icon as={Lightbulb} w={5} h={5} color="blue.600" />
                  <Heading size="md" color="blue.900">Recommendations</Heading>
                </HStack>
                <UnorderedList spacing={2}>
                  {result.recommendations.map((recommendation, index) => (
                    <ListItem key={index} color="blue.800">
                      <HStack spacing={2} align="start">
                        <Text color="blue.600" fontWeight="semibold" mt={0.5}>
                          {index + 1}.
                        </Text>
                        <Text>{recommendation}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            )}

            {result.ai_rewrite_rock && result.ai_rewrite_measurable && (
              <Box bg="purple.50" border="1px" borderColor="purple.200" borderRadius="lg" p={6}>
                <HStack spacing={2} mb={3}>
                  <Icon as={Sparkles} w={5} h={5} color="purple.600" />
                  <Heading size="md" color="purple.900">AI Suggestions</Heading>
                </HStack>
                <Text color="purple.800" mb={3}>
                  The AI has rewritten your Rock for better clarity. Would you like to see the suggestion?
                </Text>
                <Button variant="outline" colorScheme="purple" onClick={onShowRewrite}>
                  Show AI Rewrite
                </Button>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Divider mb={4} />
          <HStack spacing={3} w="full">
            <Button
              variant="ghost"
              leftIcon={<Icon as={ArrowLeft} w={4} h={4} />}
              onClick={onBackToEditing}
              flex={1}
            >
              Back to Editing
            </Button>
            {onOpenChat && (
              <Tooltip
                label={!hasAcceptedTerms ? "You must accept AI terms before using the chat feature. Click 'Evaluate' again and accept the terms when prompted." : ""}
                isDisabled={hasAcceptedTerms}
                placement="top"
              >
                <Button
                  variant="outline"
                  leftIcon={<Icon as={MessageCircle} w={4} h={4} />}
                  onClick={onOpenChat}
                  flex={1}
                  isDisabled={!hasAcceptedTerms}
                  opacity={hasAcceptedTerms ? 1 : 0.5}
                >
                  Chat with AI
                </Button>
              </Tooltip>
            )}
            <Button colorScheme="blue" onClick={onClose} flex={1}>
              Done
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

